// Path: src/pages/Landing.jsx
// Function: Main landing page with themed menu system, user authentication, and campaign management
// Changes: Fixed dynamic require, added complete header docs, extracted menu action constants, improved naming consistency

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, User, LogIn, LogOut } from 'lucide-react';
import LoginModal from '../../componets/LoginModal';
import NewCampaignModal from '../../componets/NewCampaignModal';
import LoadCampaignModal from '../../componets/LoadCampaignModal';
import { THEMES, getColorClasses } from '../themeConfig';

// ============================================================================
// Constants
// ============================================================================

const MENU_ACTIONS = {
  NEW: 'new',
  LOAD: 'load',
  SETTINGS: 'settings',
  PROFILE: 'profile',
  NEWS: 'news',
  LEADERBOARD: 'leaderboard'
};

const GLITCH_INTERVAL_MS = 3000;
const GLITCH_DURATION_MS = 100;

// ============================================================================
// Sub-Components
// ============================================================================

const UserStatusBar = ({ currentUser, config, onLogin, onLogout }) => {
  return (
    <div className="relative bg-gray-900/50 border-b border-gray-700 py-2 px-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <User className={getColorClasses(config.colors.accent)} size={18} />
        <span className="text-gray-300 text-sm font-mono">
          {currentUser ? `${config.userTitle}: ${currentUser.username.toUpperCase()}` : config.userTitleGuest}
        </span>
      </div>
      {currentUser ? (
        <button
          onClick={onLogout}
          className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-mono transition-colors"
        >
          <LogOut size={16} />
          LOGOUT
        </button>
      ) : (
        <button
          onClick={onLogin}
          className={`flex items-center gap-2 ${getColorClasses(config.colors.accent)} hover:opacity-80 text-sm font-mono transition-colors`}
        >
          <LogIn size={16} />
          SIGN IN
        </button>
      )}
    </div>
  );
};

const AlertBanner = ({ config }) => (
  <div className={`relative ${getColorClasses(config.alert.color, 'alertBg')} border-b-2 ${getColorClasses(config.alert.color, 'alertBorder')} py-2 px-4 flex items-center justify-center gap-2`}>
    <AlertTriangle className={`${getColorClasses(config.alert.color)} animate-pulse`} size={20} />
    <span className={`${getColorClasses(config.alert.color, 'alertText')} text-sm font-mono tracking-wider`}>
      {config.alert.text}
    </span>
    <AlertTriangle className={`${getColorClasses(config.alert.color)} animate-pulse`} size={20} />
  </div>
);

const Header = ({ config, isGlitchActive }) => (
  <div className="text-center mb-16">
    <div className={`transition-all duration-100 ${isGlitchActive ? 'blur-sm' : ''}`}>
      <h1 className="text-7xl font-bold mb-4 tracking-wider">
        <span className={config.title.line1Color}>{config.title.line1}</span>
        <br />
        <span className={config.title.line2Color}>{config.title.line2}</span>
      </h1>
    </div>
    <div className={`h-1 w-64 mx-auto bg-gradient-to-r from-transparent via-${config.colors.primary}-500 to-transparent mb-6`} />
    <p className="text-xl text-gray-300 font-mono">
      {config.subtitle}
    </p>
  </div>
);

const MenuItem = ({ item, isSelected, onHover, onLeave, onClick }) => (
  <button
    onClick={onClick}
    onMouseEnter={onHover}
    onMouseLeave={onLeave}
    className={`group relative w-full bg-gray-800/50 hover:bg-gray-800/80 backdrop-blur border-2 ${getColorClasses(item.color, 'border')}/50 ${getColorClasses(item.color, 'borderHover')} p-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${getColorClasses(item.color, 'shadow')}`}
  >
    <div className="flex items-center gap-4">
      <div className={`${getColorClasses(item.color, 'bg')} p-3 rounded`}>
        <item.icon className={getColorClasses(item.color)} size={32} />
      </div>
      <div className="flex-1 text-left">
        <h3 className="text-2xl font-bold tracking-wider mb-1">{item.label}</h3>
        <p className="text-sm text-gray-400">{item.subtitle}</p>
        <div className="flex items-center gap-2 mt-2">
          <div className={`w-2 h-2 rounded-full ${
            isSelected ? 'bg-green-400 animate-pulse' : 'bg-gray-600'
          }`} />
          <span className="text-xs text-gray-500 font-mono">
            {isSelected ? 'SYSTEM READY' : 'STANDBY'}
          </span>
        </div>
      </div>
      <div className="text-4xl text-gray-700 group-hover:text-gray-500 transition-colors font-mono">
        &gt;
      </div>
    </div>
  </button>
);

const SupportSection = ({ config }) => (
  <div className="max-w-2xl mx-auto mb-16">
    <div className={`bg-gradient-to-r ${getColorClasses(config.colors.accent, 'bgFrom')} ${getColorClasses(config.colors.accent, 'bgVia')} ${getColorClasses(config.colors.accent, 'bgTo')} border-2 ${getColorClasses(config.colors.accent, 'borderMain')} rounded-lg p-8 relative overflow-hidden`}>
      <div className={`absolute top-0 right-0 w-32 h-32 ${getColorClasses(config.colors.accent, 'bgAccent')} rounded-bl-full`} />
      <div className={`absolute bottom-0 left-0 w-32 h-32 ${getColorClasses(config.colors.accent, 'bgAccent')} rounded-tr-full`} />
      
      <div className="relative z-10">
        <div className="flex items-center justify-center gap-3 mb-4">
          <config.support.icon className={getColorClasses(config.colors.accent)} size={32} />
          <h2 className={`text-3xl font-bold tracking-wider ${getColorClasses(config.colors.accent)}`}>
            {config.support.title}
          </h2>
          <config.support.icon className={getColorClasses(config.colors.accent)} size={32} />
        </div>
        
        <p className="text-center text-gray-300 mb-6 font-mono text-sm whitespace-pre-line">
          {config.support.description}
        </p>

        <div className="flex justify-center">
          <a
            href="https://ko-fi.com/H2H31DPRED"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-3 ${getColorClasses(config.colors.accent, 'bg')} ${getColorClasses(config.colors.accent, 'borderHover')} text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${getColorClasses(config.colors.accent, 'shadow')}`}
          >
            <config.support.icon size={24} />
            <span className="text-lg tracking-wider">{config.support.buttonText}</span>
          </a>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <div className={`w-2 h-2 rounded-full ${getColorClasses(config.colors.accent)} animate-pulse`} />
          <span className={`text-xs ${getColorClasses(config.colors.accent, 'pulseText')} font-mono`}>
            POWERED BY KO-FI
          </span>
        </div>
      </div>
    </div>
  </div>
);

const SecondaryMenu = ({ items, onAction }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
    {items.map((item) => (
      <button
        key={item.id}
        onClick={() => onAction(item.id)}
        className="bg-gray-800/30 hover:bg-gray-800/50 border border-gray-700 hover:border-gray-600 p-4 rounded-lg transition-all duration-300 transform hover:scale-105"
      >
        <item.icon className="text-gray-400 mx-auto mb-2" size={20} />
        <h3 className="font-bold text-xs tracking-wider">{item.label}</h3>
      </button>
    ))}
  </div>
);

const Footer = ({ config, currentUser }) => (
  <div className="pt-8 border-t border-gray-800">
    <div className="flex justify-between items-center text-xs text-gray-600 font-mono">
      <div>SYSTEM STATUS: {config.footer.status}</div>
      <div>CLEARANCE: {currentUser ? config.clearanceLevel : 'GUEST'}</div>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full animate-pulse ${currentUser ? 'bg-green-500' : 'bg-yellow-500'}`} />
        {currentUser ? config.footer.readyText : config.footer.signInText}
      </div>
    </div>
  </div>
);

// ============================================================================
// Custom Hooks
// ============================================================================

const useGlitchEffect = () => {
  const [isGlitchActive, setIsGlitchActive] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsGlitchActive(true);
      setTimeout(() => setIsGlitchActive(false), GLITCH_DURATION_MS);
    }, GLITCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return isGlitchActive;
};

const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
    console.log('User logged in:', user);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  return { currentUser, handleLogin, handleLogout };
};

// ============================================================================
// Main Component
// ============================================================================

const ThemedLanding = ({ theme = 'vanilla' }) => {
  const navigate = useNavigate();
  const config = THEMES[theme] || THEMES.vanilla;
  const isGlitchActive = useGlitchEffect();
  const { currentUser, handleLogin, handleLogout } = useAuth();

  // Modal states
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState(false);
  const [isLoadCampaignModalOpen, setIsLoadCampaignModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // Menu action handlers
  const startNewGame = () => {
    if (!currentUser) {
      setIsLoginModalOpen(true);
      return;
    }
    setIsNewCampaignModalOpen(true);
  };

  const loadGame = () => {
    if (!currentUser) {
      setIsLoginModalOpen(true);
      return;
    }
    setIsLoadCampaignModalOpen(true);
  };

  const handleCreateCampaign = (campaign) => {
    console.log('Campaign created:', campaign);
    setIsNewCampaignModalOpen(false);
    navigate(`/${campaign.theme}/base`, { state: { campaign } });
  };

  const handleLoadCampaign = (campaign) => {
    console.log('Loading campaign:', campaign);
    navigate(`/${campaign.theme}/base`, { state: { campaign } });
  };

  const menuActions = {
    [MENU_ACTIONS.NEW]: startNewGame,
    [MENU_ACTIONS.LOAD]: loadGame,
    [MENU_ACTIONS.SETTINGS]: () => alert('Settings menu coming soon'),
    [MENU_ACTIONS.PROFILE]: () => alert('Profile system coming soon'),
    [MENU_ACTIONS.NEWS]: () => alert('News feed coming soon'),
    [MENU_ACTIONS.LEADERBOARD]: () => alert('Leaderboard coming soon')
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white overflow-hidden`}>
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      <UserStatusBar 
        currentUser={currentUser}
        config={config}
        onLogin={() => setIsLoginModalOpen(true)}
        onLogout={handleLogout}
      />

      <AlertBanner config={config} />

      <div className="relative max-w-6xl mx-auto px-4 py-12">
        <Header config={config} isGlitchActive={isGlitchActive} />

        {/* Main Menu */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="space-y-4">
            {config.menuItems.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                isSelected={selectedMenu === item.id}
                onHover={() => setSelectedMenu(item.id)}
                onLeave={() => setSelectedMenu(null)}
                onClick={menuActions[item.id]}
              />
            ))}
          </div>
        </div>

        <SupportSection config={config} />
        
        <SecondaryMenu 
          items={config.secondaryItems}
          onAction={(id) => menuActions[id]?.()}
        />

        <Footer config={config} currentUser={currentUser} />
      </div>

      {/* Modals */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLogin}
      />

      <NewCampaignModal
        isOpen={isNewCampaignModalOpen}
        onClose={() => setIsNewCampaignModalOpen(false)}
        onCreate={handleCreateCampaign}
        userId={currentUser?.id}
      />

      <LoadCampaignModal
        isOpen={isLoadCampaignModalOpen}
        onClose={() => setIsLoadCampaignModalOpen(false)}
        onLoad={handleLoadCampaign}
        userId={currentUser?.id}
      />
    </div>
  );
};

export default ThemedLanding;